import axios from '@/lib/axios'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  ForgotPasswordValues,
  LoginValues,
  ResetPasswordValues,
  SignUpValues,
} from './auth.schema'
import type { AuthStatus, AuthUser, FortifyStatusMessage, LoginResponse } from './auth.types'

/** The login form also sends the "Remember me" checkbox, which `loginSchema` does not cover. */
export type LoginInput = LoginValues & { remember?: boolean }

export type AuthState =
  | { isAuthenticated: true; user: AuthUser }
  | { isAuthenticated: false; user: null; error?: unknown }

// Sanctum's CSRF cookie route and Fortify's logout and register responses have empty bodies.
export const csrf = () => axios.get<void>('/sanctum/csrf-cookie')

function buildLoginPayload(value: Partial<LoginInput> = {}) {
  const payload = new URLSearchParams()

  payload.set('email', String(value?.email ?? ''))
  payload.set('password', String(value?.password ?? ''))
  payload.set('remember', value?.remember ? '1' : '0')

  return payload
}

// code 422 Unprocessable Entity is returned by auth methods that fail backend validation
// e.g. bad login credentials, signing up with an email already in use, expired password reset token, etc
export async function login(value: LoginInput) {
  await csrf()
  return axios.post<LoginResponse>('/login', buildLoginPayload(value))
}

export function status() {
  return axios.get<AuthStatus>('/auth/status')
}

export function logout() {
  return axios.post<void>('/logout')
}

export async function signUp(value: SignUpValues) {
  await csrf()
  return axios.post<void>('/register', value)
}

export async function forgotPassword(value: ForgotPasswordValues) {
  await csrf()
  return axios.post<FortifyStatusMessage>('/forgot-password', value)
}

export async function resetPassword(value: ResetPasswordValues) {
  await csrf()
  return axios.post<FortifyStatusMessage>('/reset-password', value)
}

export const nullAuthUser = {
  isAuthenticated: false,
  user: null,
} satisfies AuthState

export async function getAuthUser({
  signal,
}: Pick<QueryFunctionContext, 'signal'>): Promise<AuthState> {
  try {
    const user = await axios.get<AuthUser>('/user', { signal })
    if (user) {
      return { isAuthenticated: true, user }
    }
    return nullAuthUser
  } catch (error) {
    return { ...nullAuthUser, error }
  }
}
